import { MainMetaMap } from "@/types/meta-schema";
import { UserReduxMetadataSchema } from "@tsTypes/next-auth";
import {
  CramSheetReduxSchema,
  CustomTestReduxSchema,
  FlashcardReduxSchema,
  MockExamReduxSchema,
  NoteReduxSchema,
  QuestionReduxSchema,
  SubjectCustomTestReduxSchema,
  SubjectMockExamReduxSchema,
  SubjectSubTopicReduxSchema,
  SubjectTopicTestReduxSchema,
  SubTopicReduxSchema,
  TopicTestReduxSchema,
  UnixDay,
  VideoReduxSchema,
} from "@tsTypes/redux-schema";
import {
  AggregatedUserStreaks,
  LatestUserInteractions,
  ReduxResourceType,
  SubjectLevelCompletion,
  SubjectLevelPerformance,
  TopicLevelCompletion,
  TopicLevelPerformance,
  TopicLevelTopicTestPerformance,
  UserInteractionsData,
} from "@tsTypes/user-metadata-schema";

// Unused: year-wise grouping replaced by groupUserInteractionsByDay (flat by day).
// export function groupUserInteractionsByYear(
//   mainMetaMap: MainMetaMap,
//   subTopicUserReduxMetadata: SubjectSubTopicReduxSchema
// ) {
//   let processedDateAttemptedxSubTopicxResource = new Set<string>();
//   return Object.entries(subTopicUserReduxMetadata).reduce(
//     (acc: AggregatedUserInteractions, [subjectDocumentId, subjectData]) => {
//       if (!mainMetaMap[subjectDocumentId]) return acc;
//       if (!subjectData) return acc;
//       Object.entries(subjectData).forEach(([topicDocumentId, topicData]) => {
//         if (!topicData) return;
//         Object.entries(topicData).forEach(
//           ([subTopicDocumentId, subTopicData]) => {
//             (
//               Object.entries(subTopicData) as [
//                 ReduxResourceType,
//                 SubTopicReduxSchema[keyof SubTopicReduxSchema]
//               ][]
//             ).forEach(([resourceType, resourceData]) => {
//               if (!resourceData) return;
//               Object.values(resourceData).forEach((resourceData) => {
//                 let dateAttempted: UnixDay | null = null;
//                 if ("dv" in resourceData) dateAttempted = resourceData.dv.slice(-1)[0];
//                 else if ("dr" in resourceData) dateAttempted = resourceData.dr.slice(-1)[0];
//                 else if ("da" in resourceData) dateAttempted = resourceData.da.slice(-1)[0];
//                 else if ("dd" in resourceData) dateAttempted = resourceData.dd.slice(-1)[0];
//                 if (!dateAttempted) return;
//                 const year = unixDayToDate(dateAttempted).getFullYear();
//                 if (!acc[year]) acc[year] = {};
//                 if (!acc[year][dateAttempted]) acc[year][dateAttempted] = [];
//                 if (
//                   !processedDateAttemptedxSubTopicxResource.has(
//                     `${dateAttempted}-${subTopicDocumentId}-${resourceType}`
//                   )
//                 ) {
//                   acc[year][dateAttempted].push({
//                     subjectDocumentId,
//                     topicDocumentId,
//                     subTopicDocumentId,
//                     resourceType,
//                   });
//                   processedDateAttemptedxSubTopicxResource.add(
//                     `${dateAttempted}-${subTopicDocumentId}-${resourceType}`
//                   );
//                 }
//               });
//             });
//           }
//         );
//       });
//       return acc;
//     },
//     {}
//   );
// }

/** Flat map: day -> interactions (all years combined). */
function groupUserInteractionsByDay(
  mainMetaMap: MainMetaMap,
  subTopicUserReduxMetadata: SubjectSubTopicReduxSchema
): Record<UnixDay, UserInteractionsData[]> {
  const processedDateAttemptedxSubTopicxResource = new Set<string>();
  const acc: Record<UnixDay, UserInteractionsData[]> = {} as Record<
    UnixDay,
    UserInteractionsData[]
  >;
  for (const [subjectDocumentId, subjectData] of Object.entries(
    subTopicUserReduxMetadata
  )) {
    if (!mainMetaMap[subjectDocumentId] || !subjectData) continue;
    for (const [topicDocumentId, topicData] of Object.entries(subjectData)) {
      if (!topicData) continue;
      for (const [subTopicDocumentId, subTopicData] of Object.entries(
        topicData
      )) {
        for (const [resourceType, resourceData] of Object.entries(
          subTopicData
        ) as [
          ReduxResourceType,
          SubTopicReduxSchema[keyof SubTopicReduxSchema]
        ][]) {
          if (!resourceData) continue;
          for (const res of Object.values(resourceData) as
            | VideoReduxSchema[]
            | NoteReduxSchema[]
            | FlashcardReduxSchema[]
            | QuestionReduxSchema[]
            | CramSheetReduxSchema[]) {
            let datesAttempted: UnixDay[];
            if ("dv" in res) datesAttempted = res.dv;
            else if ("dr" in res) datesAttempted = res.dr;
            else if ("da" in res) datesAttempted = res.da;
            else if ("dd" in res) datesAttempted = res.dd;
            else continue;
            if (!datesAttempted) continue;
            // for each date attempted, add the interaction to the accumulator
            for (const date of datesAttempted) {
              if (!acc[date]) acc[date] = [];
              const key = `${date}-${subTopicDocumentId}-${resourceType}`;
              if (processedDateAttemptedxSubTopicxResource.has(key)) continue;
              acc[date].push({
                subjectDocumentId,
                topicDocumentId,
                subTopicDocumentId,
                resourceType,
              });
              processedDateAttemptedxSubTopicxResource.add(key);
            }
          }
        }
      }
    }
  }
  return acc;
}

/**
 * Gets user interaction streaks from all days (single flat result, not per year).
 */
export function getUserInteractionStreaks(
  mainMetaMap: MainMetaMap,
  subtopicUserReduxMetadata: SubjectSubTopicReduxSchema
): AggregatedUserStreaks {
  const dayEntries = groupUserInteractionsByDay(
    mainMetaMap,
    subtopicUserReduxMetadata
  );
  const today = isoStringToUnixDay(new Date().getTime());
  const sortedDays = Object.keys(dayEntries)
    .map(Number)
    .sort((a, b) => b - a);

  let longestStreak = 0;
  let streakCount = 0;
  let streakFromToday = 0;
  let streakFromTodayBroken = false;
  // let streakBroken = false;

  for (let i = 0; i < sortedDays.length; i++) {
    const day = sortedDays[i];
    const prevDay = sortedDays[i - 1];
    const isToday = day === today;
    const isConsecutive = prevDay !== undefined && day === prevDay - 1;
    if (isToday) {
      streakCount++;
      streakFromToday++;
    } else if (isConsecutive) {
      streakCount++;
      if (!streakFromTodayBroken) streakFromToday++;
      longestStreak = Math.max(longestStreak, streakCount);
    } else {
      streakFromTodayBroken = true;
      streakCount = 1;
      // streakBroken = true;
    }
  }

  const totalDaysStudied = sortedDays as UnixDay[];
  return { streakFromToday, longestStreak, totalDaysStudied };
}

/**
 * Returns the most recent user interactions (newest first), capped at limit. Stops once limit items are collected.
 * @param limit - Max number of interactions to return (default 30).
 */
export function getRecentUserInteractions(
  mainMetaMap: MainMetaMap,
  subtopicUserReduxMetadata: SubjectSubTopicReduxSchema,
  limit = 30
): LatestUserInteractions {
  const dayEntries = groupUserInteractionsByDay(
    mainMetaMap,
    subtopicUserReduxMetadata
  );
  const sortedDays = Object.keys(dayEntries)
    .map(Number)
    .sort((a, b) => b - a);
  const userInteractions: LatestUserInteractions = [];
  for (const day of sortedDays) {
    if (userInteractions.length >= limit) break;
    const interactions = dayEntries[day as UnixDay];
    for (const interaction of interactions) {
      userInteractions.push({ day: day as UnixDay, ...interaction });
      if (userInteractions.length >= limit) break;
    }
  }
  return userInteractions;
}

/**
 * Calculates completion data for a subject, aggregating data from all topics and subtopics.
 * @param {string} subjectDocumentId - The ID of the subject
 * @param {SubjectSubTopicReduxSchema} SubjectSubtopicUserReduxMetadata - Metadata for subject subtopics
 * @param {SubjectTopicTestReduxSchema} TopicTestUserReduxMetadata - Metadata for topic tests
 * @param {SubjectCustomTestReduxSchema} CustomTestUserReduxMetadata - Metadata for custom tests
 * @param {SubjectMockExamReduxSchema} MockExamUserReduxMetadata - Metadata for mock exams
 * @returns {SubjectLevelCompletion} Object containing completion data for the subject and its topics
 */
export function getSubjectCompletionData(
  subjectDocumentId: string,
  SubjectSubtopicUserReduxMetadata: SubjectSubTopicReduxSchema,
  TopicTestUserReduxMetadata: SubjectTopicTestReduxSchema,
  CustomTestUserReduxMetadata: SubjectCustomTestReduxSchema,
  MockExamUserReduxMetadata: SubjectMockExamReduxSchema
): SubjectLevelCompletion {
  // Initialize subject-level total
  const subjectTotal: SubjectLevelCompletion["total"] = {
    videos: [],
    notes: [],
    flashcards: [],
    questions: { mc: [], la: [] },
    cramSheets: [],
    topicTests: [],
    customTests: Object.keys(
      CustomTestUserReduxMetadata[subjectDocumentId] ?? {}
    ),
    mockExams: Object.keys(MockExamUserReduxMetadata[subjectDocumentId] ?? {}),
  };

  // For each topic under this subject, get topic-level completion and accumulate totals
  const topicsForSubject =
    SubjectSubtopicUserReduxMetadata[subjectDocumentId] ?? {};
  const topicResults = Object.entries(topicsForSubject).reduce(
    (
      acc: Record<string, TopicLevelCompletion | undefined>,
      [topicDocumentId, maybeTopicData]
    ) => {
      if (!maybeTopicData) {
        return acc;
      }
      const topicCompletion = getTopicCompletionData(
        subjectDocumentId,
        topicDocumentId,
        maybeTopicData,
        TopicTestUserReduxMetadata
      );

      // Accumulate into subject-level total
      subjectTotal.videos.push(...topicCompletion.total.videos);
      subjectTotal.notes.push(...topicCompletion.total.notes);
      subjectTotal.flashcards.push(...topicCompletion.total.flashcards);
      subjectTotal.questions.mc.push(...topicCompletion.total.questions.mc);
      subjectTotal.questions.la.push(...topicCompletion.total.questions.la);
      subjectTotal.cramSheets.push(...topicCompletion.total.cramSheets);
      subjectTotal.topicTests.push(...topicCompletion.total.topicTests);

      acc[topicDocumentId] = topicCompletion;
      return acc;
    },
    {} as Record<string, TopicLevelCompletion | undefined>
  );

  // Return topic breakdowns and subject-level total
  const result = {} as SubjectLevelCompletion;
  Object.assign(result, topicResults);
  result.total = subjectTotal;
  return result;
}

/**
 * Calculates completion data for a specific topic within a subject.
 * @param {string} subjectDocumentId - The ID of the subject
 * @param {string} topicDocumentId - The ID of the topic
 * @param {object} topicData - Metadata for the topic's subtopics
 * @param {SubjectTopicTestReduxSchema} TopicTestUserReduxMetadata - Metadata for topic tests
 * @returns {TopicLevelCompletion} Object containing completion data for the topic and its subtopics
 */
export function getTopicCompletionData(
  subjectDocumentId: string,
  topicDocumentId: string,
  topicData: NonNullable<
    NonNullable<SubjectSubTopicReduxSchema[string]>[string]
  >,
  TopicTestUserReduxMetadata: SubjectTopicTestReduxSchema
): TopicLevelCompletion {
  // setup total for topic level completion
  const topicTotal: TopicLevelCompletion["total"] = {
    videos: [],
    notes: [],
    flashcards: [],
    questions: { mc: [], la: [] },
    cramSheets: [],
    topicTests: Object.keys(
      TopicTestUserReduxMetadata[subjectDocumentId]?.[topicDocumentId] ?? {}
    ),
  };
  const subtopicResults = Object.entries(topicData).reduce(
    (acc, [subTopicDocumentId, subTopicData]) => {
      // get mc and la questions
      const { mcQuestions, laQuestions } = Object.entries(
        subTopicData?.questions ?? {}
      ).reduce(
        (
          acc: { mcQuestions: string[]; laQuestions: string[] },
          [questionDocumentId, questionData]
        ) => {
          if (questionData.ismc) acc.mcQuestions.push(questionDocumentId);
          else acc.laQuestions.push(questionDocumentId);
          return acc;
        },
        { mcQuestions: [], laQuestions: [] }
      );
      // get subtopic completion
      const subtopicCompletion = {
        videos: Object.keys(subTopicData?.videos ?? {}),
        notes: Object.keys(subTopicData?.notes ?? {}),
        flashcards: Object.keys(subTopicData?.flashcards ?? {}),
        questions: {
          mc: mcQuestions,
          la: laQuestions,
        },
        cramSheets: Object.keys(subTopicData?.cramSheets ?? {}),
      };

      // Accumulate totals
      topicTotal.videos.push(...subtopicCompletion.videos);
      topicTotal.notes.push(...subtopicCompletion.notes);
      topicTotal.flashcards.push(...subtopicCompletion.flashcards);
      topicTotal.questions.mc.push(...subtopicCompletion.questions.mc);
      topicTotal.questions.la.push(...subtopicCompletion.questions.la);
      topicTotal.cramSheets.push(...subtopicCompletion.cramSheets);

      return {
        ...acc,
        [subTopicDocumentId]: subtopicCompletion,
      };
    },
    {} as Omit<TopicLevelCompletion, "total">
  );

  // Add the total at the topic level
  return {
    ...subtopicResults,
    total: topicTotal,
  };
}

/**
 * Calculates performance data for a subject, aggregating data from all topics and subtopics.
 * @param {string} subjectDocumentId - The ID of the subject
 * @param {SubjectSubTopicReduxSchema} SubjectSubtopicUserReduxMetadata - Metadata for subject subtopics
 * @param {SubjectTopicTestReduxSchema} TopicTestUserReduxMetadata - Metadata for topic tests
 * @param {SubjectCustomTestReduxSchema} CustomTestUserReduxMetadata - Metadata for custom tests
 * @param {SubjectMockExamReduxSchema} MockExamUserReduxMetadata - Metadata for mock exams
 * @returns {SubjectLevelPerformance} Object containing performance data for the subject and its topics
 */
export function getSubjectPerformanceData(
  subjectDocumentId: string,
  SubjectSubtopicUserReduxMetadata: SubjectSubTopicReduxSchema,
  TopicTestUserReduxMetadata: SubjectTopicTestReduxSchema,
  CustomTestUserReduxMetadata: SubjectCustomTestReduxSchema,
  MockExamUserReduxMetadata: SubjectMockExamReduxSchema
): SubjectLevelPerformance {
  // Initialize subject-level total
  const subjectTotal: SubjectLevelPerformance["total"] = {
    all: {
      da: { mc: [], la: [] },
      perf: { mc: [], la: [] },
    },
    first: {
      mc: [],
      la: [],
    },
    customTests:
      Object.keys(CustomTestUserReduxMetadata[subjectDocumentId] ?? {})
        .length === 0
        ? undefined
        : (
            Object.entries(
              CustomTestUserReduxMetadata[subjectDocumentId] ?? {}
            ) as Array<[string, CustomTestReduxSchema]>
          ).reduce((acc, [customTestId, customTestData]) => {
            return {
              ...acc,
              [customTestId]: {
                numQuestions: customTestData.numQuestions,
                da: customTestData.da,
                perf: customTestData.perf,
                mcNum: customTestData.mcMarks,
                laNum: customTestData.numQuestions - customTestData.mcMarks,
                mcMarks: customTestData.mcMarks,
                laMarks: customTestData.laMarks,
                subTopicDocumentIds: customTestData.subTopicDocumentIds,
                freeTimes: customTestData.freeTimes,
              },
            };
          }, {}),
    mockExams:
      Object.keys(MockExamUserReduxMetadata[subjectDocumentId] ?? {}).length ===
      0
        ? undefined
        : (
            Object.entries(
              MockExamUserReduxMetadata[subjectDocumentId] ?? {}
            ) as Array<[string, MockExamReduxSchema]>
          ).reduce((acc, [mockExamId, mockExamData]) => {
            return {
              ...acc,
              [mockExamId]: {
                numQuestions: mockExamData.numQuestions,
                da: mockExamData.da,
                perf: mockExamData.perf,
                mcNum: mockExamData.mcMarks,
                laNum: mockExamData.numQuestions - mockExamData.mcMarks,
                mcMarks: mockExamData.mcMarks,
                laMarks: mockExamData.laMarks,
                subTopicDocumentIds: mockExamData.subTopicDocumentIds,
                freeTimes: mockExamData.freeTimes,
              },
            };
          }, {}),
  };

  // For each topic under this subject, get topic-level performance and accumulate totals
  const topicsForSubjectPerf =
    SubjectSubtopicUserReduxMetadata[subjectDocumentId] ?? {};
  const topicResults = Object.entries(topicsForSubjectPerf).reduce(
    (
      acc: Record<string, TopicLevelPerformance | undefined>,
      [topicDocumentId, maybeTopicData]
    ) => {
      if (!maybeTopicData) {
        return acc;
      }
      const topicProgress = getTopicPerformanceData(
        subjectDocumentId,
        topicDocumentId,
        maybeTopicData,
        TopicTestUserReduxMetadata
      );

      // Accumulate into subject-level total
      subjectTotal.all.da.mc.push(...topicProgress.total.all.da.mc);
      subjectTotal.all.da.la.push(...topicProgress.total.all.da.la);
      subjectTotal.all.perf.mc.push(...topicProgress.total.all.perf.mc);
      subjectTotal.all.perf.la.push(...topicProgress.total.all.perf.la);
      subjectTotal.first.mc.push(...topicProgress.total.first.mc);
      subjectTotal.first.la.push(...topicProgress.total.first.la);

      acc[topicDocumentId] = topicProgress;
      return acc;
    },
    {} as Record<string, TopicLevelPerformance | undefined>
  );

  // Return topic breakdowns and subject-level total
  const resultPerf = {} as SubjectLevelPerformance;
  Object.assign(resultPerf, topicResults);
  resultPerf.total = subjectTotal;
  return resultPerf;
}

/**
 * Calculates performance data for a specific topic within a subject.
 * @param {string} subjectDocumentId - The ID of the subject
 * @param {string} topicDocumentId - The ID of the topic
 * @param {object} topicData - Metadata for the topic's subtopics
 * @param {SubjectTopicTestReduxSchema} TopicTestUserReduxMetadata - Metadata for topic tests
 * @returns {TopicLevelPerformance} Object containing performance data for the topic and its subtopics
 */
export function getTopicPerformanceData(
  subjectDocumentId: string,
  topicDocumentId: string,
  topicData: {
    [subTopicDocumentId: string]: SubTopicReduxSchema;
  },
  TopicTestUserReduxMetadata: SubjectTopicTestReduxSchema
): TopicLevelPerformance {
  // setup total for topic level completion
  const topicTotal: TopicLevelPerformance["total"] = {
    all: {
      da: { mc: [], la: [] },
      perf: { mc: [], la: [] },
    },
    first: {
      mc: [],
      la: [],
    },
    topicTests:
      Object.keys(
        TopicTestUserReduxMetadata[subjectDocumentId]?.[topicDocumentId] ?? {}
      ).length === 0
        ? undefined
        : (
            Object.entries(
              TopicTestUserReduxMetadata[subjectDocumentId]?.[
                topicDocumentId
              ] ?? {}
            ) as Array<[string, TopicTestReduxSchema]>
          ).reduce((acc, [topicTestId, topicTestData]) => {
            return {
              ...acc,
              [topicTestId]: topicTestData as TopicLevelTopicTestPerformance,
            };
          }, {}),
  };
  const subtopicResults = Object.entries(topicData).reduce(
    (acc, [subTopicDocumentId, subTopicData]) => {
      const subTopicProgress = Object.values(
        subTopicData?.questions ?? {}
      ).reduce(
        (acc, question) => {
          const allPerformances = question.perf;
          const pushLocation = question.ismc ? "mc" : "la";
          acc.first[pushLocation].push(allPerformances[0]);
          acc.all.da[pushLocation].push(...question.da);
          acc.all.perf[pushLocation].push(...allPerformances);
          return acc;
        },
        {
          all: {
            da: { mc: [] as UnixDay[], la: [] as UnixDay[] },
            perf: { mc: [] as number[], la: [] as number[] },
          },
          first: {
            mc: [] as number[],
            la: [] as number[],
          },
        }
      );

      // Accumulate totals
      topicTotal.all.da.mc.push(...subTopicProgress.all.da.mc);
      topicTotal.all.da.la.push(...subTopicProgress.all.da.la);
      topicTotal.all.perf.mc.push(...subTopicProgress.all.perf.mc);
      topicTotal.all.perf.la.push(...subTopicProgress.all.perf.la);
      topicTotal.first.mc.push(...subTopicProgress.first.mc);
      topicTotal.first.la.push(...subTopicProgress.first.la);

      return {
        ...acc,
        [subTopicDocumentId]: subTopicProgress,
      };
    },
    {} as Omit<TopicLevelPerformance, "total">
  );

  // Add the total at the topic level
  return {
    ...subtopicResults,
    total: topicTotal,
  };
}

/**
 * Converts a timestamp in milliseconds to a Unix day.
 * @param {number} timestampMs - Timestamp in milliseconds
 * @returns {UnixDay} Unix day number
 */
export function isoStringToUnixDay(timestampMs: number): UnixDay {
  return Math.floor(timestampMs / 1000 / 86400) as UnixDay;
}

/**
 * Display-only: maps a calendar cell {@link Date} (e.g. Mantine DatePicker) to a
 * {@link UnixDay} comparable to stored streak keys from {@link isoStringToUnixDay} on real
 * timestamps. Uses local noon so the cell’s local calendar day does not map to the
 * previous UTC day when local midnight would.
 */
export function unixDayComparableFromCalendarDate(date: Date): UnixDay {
  const noon = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
    0
  );
  return isoStringToUnixDay(noon.getTime());
}

/**
 * Converts a Unix day to a JavaScript Date object.
 * @param {UnixDay} unixDay - Unix day number
 * @returns {Date} Date object representing the start of the Unix day
 */
export function unixDayToDate(unixDay: UnixDay): Date {
  return new Date(unixDay * 1000 * 86400);
}

/**
 * Fills in missing metadata for a subtopic resource in the Redux store.
 * @param {SubjectSubTopicReduxSchema} state - Current Redux state
 * @param {string} subjectDocumentId - The ID of the subject
 * @param {string} topicDocumentId - The ID of the topic
 * @param {string} subTopicDocumentId - The ID of the subtopic
 * @param {string} resourceKey - The type of resource to add
 * @param {string} resourceDocumentId - The ID of the resource to add
 * @returns {void}
 */
export function fillMissingSubtopicUserReduxMetadata(
  state: SubjectSubTopicReduxSchema,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  resourceKey:
    | "videos"
    | "notes"
    | "flashcards"
    | "cramSheets"
    | "mcQuestions"
    | "laQuestions",
  resourceDocumentId: string
) {
  const indexableKey: Exclude<
    ReduxResourceType,
    "definitions" | "customTests" | "topicTests"
  > =
    resourceKey === "mcQuestions" || resourceKey === "laQuestions"
      ? "questions"
      : resourceKey;
  if (!state[subjectDocumentId]) {
    state[subjectDocumentId] = createMissingSubject();
  }
  if (!state[subjectDocumentId][topicDocumentId]) {
    state[subjectDocumentId][topicDocumentId] = createMissingTopic();
  }
  if (!state[subjectDocumentId][topicDocumentId][subTopicDocumentId]) {
    state[subjectDocumentId][topicDocumentId][subTopicDocumentId] =
      createMissingSubtopic();
  }
  // create resource props at subtopic level if doesn't already exist (covers where subtopic was created from another resource already)
  if (
    !state[subjectDocumentId][topicDocumentId][subTopicDocumentId][indexableKey]
  ) {
    state[subjectDocumentId][topicDocumentId][subTopicDocumentId][
      indexableKey
    ] = {
      [resourceDocumentId]: getBlankResource(indexableKey) as any,
    };
  }
  // create resource if doesn't already exist (covers where overall resource prop was created from another identical resource already)
  else if (
    !state[subjectDocumentId][topicDocumentId][subTopicDocumentId][
      indexableKey
    ][resourceDocumentId]
  ) {
    state[subjectDocumentId][topicDocumentId][subTopicDocumentId][indexableKey][
      resourceDocumentId
    ] = getBlankResource(indexableKey) as any;
  }
}

/**
 * Creates a new empty subject object in the Redux store.
 * @returns {object} Empty subject object with default values
 */
export function createMissingSubject() {
  return {};
}

/**
 * Creates a new empty topic object in the Redux store.
 * @returns {object} Empty topic object with default values
 */
export function createMissingTopic() {
  return {};
}

/**
 * Creates a new empty subtopic object in the Redux store.
 * @returns {object} Empty subtopic object with default values for all resource types
 */
export function createMissingSubtopic() {
  return {
    videos: undefined,
    notes: undefined,
    flashcards: undefined,
    questions: undefined,
    cramSheets: undefined,
  };
}

/**
 * Creates a blank resource object for a given resource type.
 * @param {Exclude<ReduxResourceType, "definitions">} resourceType - The type of resource to create
 * @returns {object} Empty resource object with appropriate default values
 */
export function getBlankResource(
  resourceType: Exclude<ReduxResourceType, "definitions">
) {
  switch (resourceType) {
    case "videos":
      return {
        num: 0,
        dur: "00:00",
        wt: 0,
        dv: [],
        df: [],
      } as VideoReduxSchema;
    case "notes":
      return { num: 0, dr: [] } as NoteReduxSchema;
    case "flashcards":
      return { dv: [], ds: [] } as FlashcardReduxSchema;
    case "questions":
      return {
        ismc: false,
        da: [],
        perf: [],
      } as QuestionReduxSchema;
    case "cramSheets":
      return { num: 0, dd: [] } as CramSheetReduxSchema;
    case "topicTests":
      return {
        num: 0,
        freeTimes: [],
        da: [],
        perf: [],
      } as TopicTestReduxSchema;
    case "customTests":
    case "mockExams":
      return {
        numQuestions: 0,
        mcMarks: 0,
        laMarks: 0,
        subTopicDocumentIds: [],
        freeTimes: [],
        da: [],
        perf: [],
      } as CustomTestReduxSchema;
  }
}

export async function saveUserProgressToDB(
  userDocumentId: string,
  documentId: string,
  key: string,
  UserReduxMetadata: UserReduxMetadataSchema
) {
  const res = await fetch("/api/strapi/saveProgress", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userDocumentId,
      documentId,
      key,
      data: UserReduxMetadata,
    }),
  });
  return (await res.json()) as {
    key: string;
    documentId: string;
    data: UserReduxMetadataSchema;
    error?: string;
  };
}

export async function getUserProgressDataFromDB(userDocumentId: string) {
  const res = await fetch("/api/strapi/retrieveProgress", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userDocumentId,
    }),
  });
  return (await res.json()) as {
    key: string;
    documentId: string;
    data: UserReduxMetadataSchema;
  };
}
